/**
 * Vendor Overview — India map (multi-state vendor) + 10-item detail panel.
 * FR-V01 to FR-V03
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import VendorIndiaMap from './VendorIndiaMap';
import {
  vendorKPIs,
  vendorPortfolioPanel,
  vendorStatePanels,
} from '../../data/mockData';

const prCls = (pr: number) =>
  pr >= 75 ? 'text-green-600 font-bold' :
  pr >= 65 ? 'text-amber-600 font-bold' :
             'text-red-600 font-bold';

function DetailRow({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-text-tertiary">{label}</span>
      <span className={`text-xs font-semibold text-text-primary ${valueClass ?? ''}`}>{value}</span>
    </div>
  );
}

export default function VendorOverview() {
  const [selectedState, setSelectedState] = useState<string | null>(null);

  const stateData = selectedState
    ? vendorStatePanels.find(s => s.name === selectedState) ?? null
    : null;

  const panel = stateData
    ? {
        title:            stateData.name,
        totalPlants:      stateData.totalPlants.toLocaleString(),
        capacity:         stateData.capacity,
        todayGeneration:  stateData.todayGeneration,
        specificYield:    `${stateData.specificYield.toFixed(2)} kWh/kWp`,
        avgPR:            stateData.avgPR,
        avgCUF:           `${stateData.avgCUF.toFixed(1)}%`,
        plantAvailability:`${stateData.plantAvailability.toFixed(1)}%`,
        activePlants:     stateData.activePlants.toLocaleString(),
        offlinePlants:    stateData.offlinePlants.toLocaleString(),
        offlineIsRed:     stateData.offlinePlants > 0,
        activeAlarms:     stateData.activeAlarms.toLocaleString(),
        alarmsIsRed:      stateData.activeAlarms > 0,
      }
    : {
        title:            'Zebron Solar Portfolio',
        totalPlants:      vendorPortfolioPanel.totalPlants.toLocaleString(),
        capacity:         vendorPortfolioPanel.capacity,
        todayGeneration:  vendorPortfolioPanel.todayGeneration,
        specificYield:    `${vendorPortfolioPanel.specificYield.toFixed(2)} kWh/kWp`,
        avgPR:            vendorPortfolioPanel.avgPR,
        avgCUF:           `${vendorPortfolioPanel.avgCUF.toFixed(1)}%`,
        plantAvailability:`${vendorPortfolioPanel.plantAvailability.toFixed(1)}%`,
        activePlants:     vendorPortfolioPanel.activePlants.toLocaleString(),
        offlinePlants:    vendorPortfolioPanel.offlinePlants.toLocaleString(),
        offlineIsRed:     vendorPortfolioPanel.offlinePlants > 0,
        activeAlarms:     vendorPortfolioPanel.activeAlarms.toLocaleString(),
        alarmsIsRed:      vendorPortfolioPanel.activeAlarms > 0,
      };

  return (
    <div className="p-6 space-y-6">

      {/* ── 6 Primary KPI cards ── */}
      <div className="flex gap-4">
        <KPICard title="Total Plants"       value={vendorKPIs.totalPlants.toLocaleString()} subtext="Zebron Solar portfolio" />
        <KPICard title="Installed Capacity" value={`${vendorKPIs.totalCapacityMWp} MWp`}   subtext="Installed" />
        <KPICard title="Today's Generation" value={vendorPortfolioPanel.todayGeneration}    subtext="+2.1% vs yesterday" subtextColor="green" />
        <KPICard title="Active Plants"      value={vendorKPIs.activePlants.toLocaleString()} subtext={`${((vendorKPIs.activePlants / vendorKPIs.totalPlants) * 100).toFixed(0)}% of portfolio`} subtextColor="green" />
        <KPICard title="Offline / Fault"    value={vendorKPIs.offlinePlants.toLocaleString()} subtext="Needs attention" subtextColor="red" bgColor="bg-red-50" />
        <KPICard title="Avg Performance Ratio" value={`${vendorKPIs.avgPrPct}%`}            subtext="Portfolio average" subtextColor="green" />
      </div>

      {/* ── Map (65%) + Detail Panel (35%) ── */}
      <div className="flex gap-4" style={{ minHeight: 460 }}>

        {/* Left: India choropleth map */}
        <div className="bg-white rounded-card border border-border shadow-card p-4" style={{ flex: '0 0 65%' }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-xs font-semibold text-text-primary">Portfolio — India</p>
              <p className="text-xs text-text-tertiary">Click a state to view details</p>
            </div>
          </div>
          <div style={{ height: 380 }}>
            <VendorIndiaMap
              onStateSelect={setSelectedState}
              selectedState={selectedState}
            />
          </div>
        </div>

        {/* Right: Detail panel */}
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex flex-col" style={{ flex: '0 0 35%' }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-text-primary">{panel.title}</p>
            {selectedState && (
              <button
                onClick={() => setSelectedState(null)}
                className="p-1 rounded hover:bg-bg-secondary text-text-tertiary hover:text-text-primary transition-colors"
                title="Reset to portfolio view"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {selectedState && (
            <p className="text-xs text-text-tertiary mb-3 -mt-1">
              Click ✕ to return to portfolio view
            </p>
          )}

          <div className="flex-1 space-y-0">
            <DetailRow label="Total Plants"       value={panel.totalPlants} />
            <DetailRow label="Installed Capacity" value={panel.capacity} />
            <DetailRow label="Today's Generation" value={panel.todayGeneration} />
            <DetailRow label="Specific Yield"     value={panel.specificYield} />
            <DetailRow
              label="Avg PR"
              value={`${panel.avgPR}%`}
              valueClass={prCls(panel.avgPR)}
            />
            <DetailRow label="Avg CUF"             value={panel.avgCUF} />
            <DetailRow label="Plant Availability"  value={panel.plantAvailability} />
            <DetailRow label="Active Plants"       value={panel.activePlants} />
            <DetailRow
              label="Offline Plants"
              value={panel.offlinePlants}
              valueClass={panel.offlineIsRed ? 'text-red-600' : ''}
            />
            <DetailRow
              label="Active Alarms"
              value={panel.activeAlarms}
              valueClass={panel.alarmsIsRed ? 'text-red-600' : ''}
            />
          </div>
        </div>
      </div>

      {/* ── 3 Secondary KPI cards: Avg CUF · Avg Specific Yield · Plant Availability ── */}
      <div className="flex gap-4">
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg CUF Today</p>
          <p className="text-xl font-bold text-text-primary mt-1">{vendorKPIs.avgCufPct}%</p>
          <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Avg Specific Yield</p>
          <p className="text-xl font-bold text-text-primary mt-1">{vendorPortfolioPanel.specificYield.toFixed(2)} kWh/kWp</p>
          <p className="text-xs text-text-secondary mt-1">Portfolio avg · current month</p>
        </div>
        <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
          <p className="text-xs text-text-secondary font-semibold uppercase tracking-wide">Plant Availability</p>
          <p className="text-xl font-bold text-text-primary mt-1">{vendorPortfolioPanel.plantAvailability.toFixed(1)}%</p>
          <p className="text-xs text-text-secondary mt-1">Solar hours basis</p>
        </div>
      </div>

    </div>
  );
}
